import { apiClient } from './apiClient';
import type { BookDto } from '../types/book';
import type {
  BookFilterState,
  BulkOperationResultDto,
  BulkQuotaUpdateDto,
  BulkStatusUpdateDto,
  EpubRendererType,
  ManageBooksListResult,
  UpdateBookManageDto,
} from '../types/manageBooks';

const BASE = '/api/app/books-extn/manage';

function toQuery(f: BookFilterState): Record<string, string | number | boolean> {
  const q: Record<string, string | number | boolean> = {
    skipCount: (f.page - 1) * f.pageSize,
    maxResultCount: f.pageSize,
    sorting: `${f.sort ?? 'creationTime'} ${f.dir ?? 'desc'}`,
  };
  if (f.q) q.filterText = f.q;
  if (f.status !== undefined) q.status = f.status;
  if (f.format !== undefined) q.format = f.format;
  if (f.publisherId) q.publisherId = f.publisherId;
  if (f.drm !== undefined) q.isDrmEnabled = f.drm;
  if (f.rendererType !== undefined) q.rendererType = f.rendererType;
  if (f.complexityMin !== undefined) q.complexityMin = f.complexityMin;
  if (f.complexityMax !== undefined) q.complexityMax = f.complexityMax;
  if (f.createdAfter) q.createdAfter = f.createdAfter;
  if (f.createdBefore) q.createdBefore = f.createdBefore;
  if (f.hasCover !== undefined) q.hasCover = f.hasCover;
  if (f.includeDeleted) q.includeDeleted = true;
  if (f.includeArchived) q.includeArchived = true;
  return q;
}

export const booksManageService = {
  list: async (filters: BookFilterState): Promise<ManageBooksListResult> => {
    const { data } = await apiClient.get<ManageBooksListResult>(`${BASE}/list`, {
      params: toQuery(filters),
    });
    return data;
  },

  idsForFilter: async (filters: BookFilterState): Promise<string[]> => {
    const { data } = await apiClient.get<string[]>(`${BASE}/ids-for-filter`, {
      params: toQuery(filters),
    });
    return data;
  },

  get: async (id: string): Promise<BookDto> => {
    const { data } = await apiClient.get<BookDto>(`${BASE}/${id}`);
    return data;
  },

  update: async (id: string, input: UpdateBookManageDto): Promise<BookDto> => {
    const { data } = await apiClient.put<BookDto>(`${BASE}/${id}`, input);
    return data;
  },

  replaceCover: async (id: string, file: File): Promise<BookDto> => {
    const form = new FormData();
    form.append('coverImage', file);
    const { data } = await apiClient.post<BookDto>(`${BASE}/${id}/cover`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`${BASE}/${id}`);
  },

  restore: async (id: string): Promise<BookDto> => {
    const { data } = await apiClient.post<BookDto>(`${BASE}/${id}/restore`);
    return data;
  },

  bulkStatus: async (input: BulkStatusUpdateDto): Promise<BulkOperationResultDto> => {
    const { data } = await apiClient.post<BulkOperationResultDto>(`${BASE}/bulk/status`, input);
    return data;
  },

  bulkQuotas: async (input: BulkQuotaUpdateDto): Promise<BulkOperationResultDto> => {
    const { data } = await apiClient.post<BulkOperationResultDto>(`${BASE}/bulk/quotas`, input);
    return data;
  },

  bulkDelete: async (bookIds: string[]): Promise<BulkOperationResultDto> => {
    const { data } = await apiClient.post<BulkOperationResultDto>(`${BASE}/bulk/delete`, bookIds);
    return data;
  },

  exportExcel: async (filters: BookFilterState): Promise<Blob> => {
    const body = {
      skipCount: 0,
      maxResultCount: 10000,
      sorting: `${filters.sort ?? 'creationTime'} ${filters.dir ?? 'desc'}`,
      filterText: filters.q,
      status: filters.status,
      format: filters.format,
      publisherId: filters.publisherId,
      isDrmEnabled: filters.drm,
      rendererType: filters.rendererType,
      complexityMin: filters.complexityMin,
      complexityMax: filters.complexityMax,
      createdAfter: filters.createdAfter,
      createdBefore: filters.createdBefore,
      hasCover: filters.hasCover,
      includeDeleted: filters.includeDeleted ?? false,
      includeArchived: filters.includeArchived ?? false,
    };
    const { data } = await apiClient.post(`${BASE}/export-excel`, body, {
      responseType: 'blob',
    });
    return data as Blob;
  },

  /**
   * Trigger the fork-on-switch pipeline for a book. Backend creates a new
   * Book record (with a new ID) using the target renderer, archives the old
   * book (IsActive=false, SupersededByBookId=newId, BookFamilyId preserved),
   * and enqueues CFI + search-index Hangfire jobs.
   *
   * Returns the new book DTO. Synchronous at the HTTP level — may take 1-2
   * minutes to return while the re-upload pipeline runs.
   */
  switchRenderer: async (bookId: string, targetType: EpubRendererType): Promise<BookDto> => {
    const { data } = await apiClient.post<BookDto>(
      `/api/app/books-extn/switch-renderer/${bookId}`,
      { targetRendererType: targetType }
    );
    return data;
  },
};
